import { BaseService } from './BaseService';
import { API_ENDPOINTS } from './config';
import { ArtifactTree } from '../../types/artifacts';
import { PrimeArtifact } from '../../types/primeModel';
import { ArtifactService } from './artifactService';

export interface ThreadModelOption {
  id: string;
  name: string;
  type: 'prime' | 'company';
  description?: string;
}

export interface BusinessModel {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  artifact_tree_id: string;
}

export class ThreadModelService extends BaseService {
  /**
   * Get root prime artifacts for Prime model selection
   */
  static async getPrimeRoots(): Promise<PrimeArtifact[]> {
    try {
      return this.get<PrimeArtifact[]>(API_ENDPOINTS.PRIME_ARTIFACTS.ROOTS);
    } catch (error) {
      console.error('Error fetching prime root artifacts:', error);
      return [];
    }
  }

  /**
   * Get artifact trees for Company model selection
   * Filters by user's accessible companies if provided
   */
  static async getCompanyTrees(
    companyId?: string,
    accessibleCompanyIds?: string[]
  ): Promise<ArtifactTree[]> {
    try {
      // Use ArtifactService.getTrees() which handles company filtering
      return await ArtifactService.getTrees(companyId, accessibleCompanyIds);
    } catch (error) {
      console.error('Error fetching company artifact trees:', error);
      return [];
    }
  }

  /**
   * Get business models for a specific artifact tree
   */
  static async getBusinessModelsForTree(artifactTreeId: string): Promise<BusinessModel[]> {
    try {
      const response = await this.get<BusinessModel[]>(`/business-models/tree/${artifactTreeId}`);
      return response;
    } catch (error) {
      // If no business models found or error, return empty array
      console.log('No business models found for artifact tree, using default');
      return [];
    }
  }

  /**
   * Get all available model options for thread creation
   * Filters company trees by user's accessible companies if provided
   */
  static async getAllModelOptions(
    companyId?: string,
    accessibleCompanyIds?: string[]
  ): Promise<{
    primeRoots: PrimeArtifact[];
    companyTrees: ArtifactTree[];
  }> {
    try {
      const [primeRoots, companyTrees] = await Promise.all([
        this.getPrimeRoots(),
        this.getCompanyTrees(companyId, accessibleCompanyIds),
      ]);

      return {
        primeRoots,
        companyTrees,
      };
    } catch (error) {
      console.error('Error fetching model options:', error);
      return {
        primeRoots: [],
        companyTrees: [],
      };
    }
  }
}
